import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/hooks/useAuth';
import { companiesApi } from '@/lib/companies';
import { api } from '@/lib/api';

interface WorkspaceLite { id: string; name: string; companyId: string }

export function AppHome() {
  const { t } = useTranslation();
  const { data: user, isLoading } = useCurrentUser();
  const { data: companies, isLoading: companiesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
    enabled: !!user,
  });
  const companyIds = companies?.map((c) => c.id) ?? [];
  // Workspaces across ALL of the user's companies (not just companies[0]).
  const { data: workspaces, isLoading: wsLoading } = useQuery({
    queryKey: ['workspaces', 'all', companyIds.join(',')],
    queryFn: async () => {
      const lists = await Promise.all(
        companyIds.map((id) =>
          api.get<WorkspaceLite[]>('/api/workspaces', { params: { companyId: id } }).then((r) => r.data),
        ),
      );
      return lists.flat();
    },
    enabled: companyIds.length > 0,
  });

  if (isLoading || companiesLoading || (companyIds.length > 0 && wsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-500 text-sm">
        {t('common.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }
  if (!user) return <Navigate to="/?auth=required" replace />;
  if (!companies || companies.length === 0) return <Navigate to="/onboarding" replace />;
  // Restore the last-visited workspace if it's still accessible — skips the
  // chooser for returning multi-workspace users (F-034).
  const lastWsId = typeof window !== 'undefined' ? window.localStorage.getItem('knowlez.lastWsId') : null;
  if (lastWsId && workspaces?.some((w) => w.id === lastWsId)) {
    return <Navigate to={`/app/workspace/${lastWsId}`} replace />;
  }
  // Exactly one workspace → go straight in. More than one → let the user choose.
  if (workspaces && workspaces.length === 1) return <Navigate to={`/app/workspace/${workspaces[0].id}`} replace />;

  if (workspaces && workspaces.length > 1) {
    return (
      <div className="min-h-screen paper-archive flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <h1 className="display text-2xl font-semibold text-ink-900 mb-1">
            {t('app.choose_workspace', { defaultValue: 'Choose a workspace' })}
          </h1>
          <p className="text-[13.5px] text-ink-600 mb-5">
            {t('app.choose_workspace_sub', { defaultValue: 'Pick where you want to work today.' })}
          </p>
          <ul className="space-y-2">
            {workspaces.map((w) => (
              <li key={w.id}>
                <Link
                  to={`/app/workspace/${w.id}`}
                  className="card-paper flex items-center justify-between px-4 py-3 hover:shadow-card hover:-translate-y-0.5 transition-all"
                >
                  <span className="font-medium text-ink-900">{w.name}</span>
                  <span className="text-accent-600">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-ink-500 text-sm">
      {t('app.no_workspaces', { defaultValue: 'No workspaces yet.' })}{' '}
      <a href="/onboarding" className="ml-1 text-indigo-600 underline">
        {t('app.set_one_up', { defaultValue: 'Set one up' })}
      </a>
      .
    </div>
  );
}
